import {
  openNotificationStream as openStream,
  type NotificationStreamHandlers,
} from '@gokrew/ui/utility/notificationStream';
import { buildNotificationActionBody } from '@gokrew/ui/utility/notifications';
import api from './client';
import { tokenManager } from '@/auth';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  DeleteNotificationResponse,
  GetNotificationsParams,
  MarkAllNotificationsReadResponse,
  MarkNotificationReadResponse,
  NotificationActionRequest,
  NotificationFeed,
  NotificationUnreadCount,
} from '@gokrew/models/notifications';

// The employee routes carry no company segment — the backend resolves the
// company from the token.
const base = '/employee/notifications';

const endpoints = {
  feed: () => base,
  unreadCount: () => `${base}/unread-count`,
  stream: () => `${base}/stream`,
  markRead: (id: string) => `${base}/${id}/read`,
  markAllRead: () => `${base}/read-all`,
  remove: (id: string) => `${base}/${id}`,
};

/* ── Feed ────────────────────────────────────────────────────────────────── */

export const getNotifications = async (params: GetNotificationsParams = {}) => {
  const { data } = await api.get<ApiResponse<NotificationFeed>>(
    endpoints.feed(),
    { params }
  );
  return data.data;
};

export const getNotificationsUnreadCount = async () => {
  const { data } = await api.get<ApiResponse<NotificationUnreadCount>>(
    endpoints.unreadCount()
  );
  return data.data;
};

export const markNotificationRead = async (id: string) => {
  const { data } = await api.patch<ApiResponse<MarkNotificationReadResponse>>(
    endpoints.markRead(id)
  );
  return data.data;
};

export const markAllNotificationsRead = async () => {
  const { data } = await api.patch<
    ApiResponse<MarkAllNotificationsReadResponse>
  >(endpoints.markAllRead());
  return data.data;
};

export const deleteNotification = async (id: string) => {
  const { data } = await api.delete<ApiResponse<DeleteNotificationResponse>>(
    endpoints.remove(id)
  );
  return data.data;
};

/* ── Inline actions ──────────────────────────────────────────────────────── */

/** Fires an action's own resolved request, at the path the action carries. */
export const runNotificationAction = async (
  request: NotificationActionRequest,
  promptValue?: string
) => {
  const { data } = await api.request<ApiResponse<unknown>>({
    method: request.method,
    url: request.path,
    data: buildNotificationActionBody(request, promptValue),
  });
  return data.data;
};

/* ── SSE stream ──────────────────────────────────────────────────────────── */

/**
 * No language is threaded in: the stream endpoint takes none, and the copy on
 * its frames is never rendered — the panel re-reads the feed over REST, where
 * the interceptor sends the reader's language.
 */
export const openNotificationStream = (
  signal: AbortSignal,
  handlers: NotificationStreamHandlers
) =>
  openStream(
    `/api/v1${endpoints.stream()}`,
    () => tokenManager.getValidToken(),
    signal,
    handlers
  );
